import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../common/Button';
import Card from '../common/Card';
import Table from '../common/Table';
import Modal from '../common/Modal';
import './VerifyVendors.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const VerifyVendors = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const getToken = () => localStorage.getItem('marilink_token');

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/admin/vendors`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setVendors(data.vendors);
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (vendorId) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/admin/vendors/${vendorId}/verify`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        alert('Vendor verified successfully');
        fetchVendors();
      } else {
        alert(data.message);
      }
    } catch (error) {
      alert('Error verifying vendor: ' + error.message);
    }
  };

  const handleReject = async (vendorId) => {
    if (!window.confirm('Reject this vendor application?')) return;
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/admin/vendors/${vendorId}/reject`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        alert('Vendor rejected');
        fetchVendors();
      } else {
        alert(data.message);
      }
    } catch (error) {
      alert('Error rejecting vendor: ' + error.message);
    }
  };

  const filteredVendors = vendors.filter(vendor => {
    const matchesSearch = vendor.company?.toLowerCase().includes(searchTerm.toLowerCase()) || vendor.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || (vendor.isVerified ? 'verified' : 'pending') === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: vendors.length,
    pending: vendors.filter(v => !v.isVerified).length,
    verified: vendors.filter(v => v.isVerified).length
  };

  const columns = [
    { key: 'company', label: 'Company Name', sortable: true },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'createdAt', label: 'Submitted', render: (val) => new Date(val).toLocaleDateString() },
    { key: 'isVerified', label: 'Status', render: (val) => <span className={`status-badge ${val ? 'verified' : 'pending'}`}>{val ? 'Verified' : 'Pending'}</span> }
  ];

  const actions = [
    { icon: 'fa-eye', label: 'View Details', onClick: (row) => { setSelectedVendor(row); setShowDetailsModal(true); } },
    { icon: 'fa-check', label: 'Verify', onClick: (row) => handleVerify(row._id), className: 'btn-verify' },
    { icon: 'fa-times', label: 'Reject', onClick: (row) => handleReject(row._id), className: 'btn-reject' }
  ];

  if (loading) return <div className="loading-spinner">Loading vendors...</div>;

  return (
    <div className="admin-vendors-page">
      <div className="page-header">
        <h1><i className="fas fa-user-check"></i> Verify Service Providers</h1>
        <p>Review and verify vendor applications</p>
      </div>

      <div className="stats-grid">
        <Card title="Total Applications" value={stats.total} icon="fa-building" />
        <Card title="Pending Review" value={stats.pending} icon="fa-clock" color="warning" />
        <Card title="Verified" value={stats.verified} icon="fa-check-circle" color="success" />
      </div>

      <div className="filters-bar">
        <div className="filter-tabs">
          <button className={`filter-tab ${filterStatus === 'all' ? 'active' : ''}`} onClick={() => setFilterStatus('all')}>All ({stats.total})</button>
          <button className={`filter-tab ${filterStatus === 'pending' ? 'active' : ''}`} onClick={() => setFilterStatus('pending')}>Pending ({stats.pending})</button>
          <button className={`filter-tab ${filterStatus === 'verified' ? 'active' : ''}`} onClick={() => setFilterStatus('verified')}>Verified ({stats.verified})</button>
        </div>
        <div className="search-box"><i className="fas fa-search"></i><input type="text" placeholder="Search vendors..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} /></div>
      </div>

      <Table columns={columns} data={filteredVendors} actions={actions} emptyMessage="No vendors found" />

      <Modal isOpen={showDetailsModal} onClose={() => setShowDetailsModal(false)} title="Vendor Details">
        {selectedVendor && (
          <div className="vendor-details">
            <div className="detail-row"><strong>Company:</strong> {selectedVendor.company}</div>
            <div className="detail-row"><strong>Email:</strong> {selectedVendor.email}</div>
            <div className="detail-row"><strong>Phone:</strong> {selectedVendor.phone}</div>
            <div className="detail-row"><strong>Registration No:</strong> {selectedVendor.registrationNo || 'N/A'}</div>
            <div className="detail-row"><strong>Submitted:</strong> {new Date(selectedVendor.createdAt).toLocaleDateString()}</div>
            <div className="detail-row"><strong>Status:</strong> {selectedVendor.isVerified ? 'Verified' : 'Pending'}</div>
            <div className="detail-row"><strong>Documents:</strong> {selectedVendor.documents?.length || 0} uploaded</div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default VerifyVendors;